import React, { useState, useEffect } from "react";
import CouponCodes from "../../../dialog/couponCodes";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import RewardsService from "../../../../services/reward";
import {
  successMessageConstant,
  couponTypes,
  keyConstants
} from "../../../../constants";
import Utils from "../../../../utility";
import {
  hideLoader,
  removeUser,
  showLoader
} from "../../../../slices/userSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import CustomPagination from "../../../../common/components/customPagination";
import DeleteCoupon from "../../../dialog/deleteCoupon";
import moment from "moment";
import AddCoupon from "../../../../modules/dialog/addCoupon";

const PREFIX = "CouponInventory";

const classes = {
  tableHead: `${PREFIX}-tableHead`,
  tableBody: `${PREFIX}-tableBody`
};

const Root = styled("div")({
  [`& .${classes.tableHead}`]: {
    "& .MuiTableCell-head": {
      padding: "10px 0px 10px 30px"
    }
  },
  [`& .${classes.tableBody}`]: {
    "& .MuiTableCell-body": {
      padding: "10px 0px 10px 30px"
    }
  }
});

function CouponInventory() {
  const [showPopup, setShowPopup] = useState(false);
  const [couponDatas, setCouponDatas] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [showPopups, setShowPopups] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [editCodes, setEditCodes] = useState([]);
  const [isDeleteCoupon, setIsDeleteCoupon] = useState(false);
  const [deleteCouponID, setDeleteCouponID] = useState("");
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const togglePopup = (item) => {
    setEditValues(item);
    setShowPopup(!showPopup);
  };

  const togglePopups = (item) => {
    setEditCodes(item);
    setShowPopups(!showPopups);
  };

  const [page, setPage] = useState(1);
  const itemsPerPage = 5; // page limit
  const [totalItems, setTotalItems] = useState(0);

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const capitalizeFirstLetter = (str) => {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const fetchCouponData = async (skip, limit) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(fetchCouponData);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    try {
      dispatch(showLoader());
      const businessId = user._id;
      const [error, response] = await Utils.parseResponse(
        new RewardsService().getCouponsByBusinessId(businessId, skip, limit)
      );
      if (error) {
        console.error(error);
      } else {
        const formattedData =
          response?.data?.length > 0 &&
          response?.data?.map((item) => ({
            id: item?._id,
            discountValue: item?.discountValue,
            couponType: capitalizeFirstLetter(item?.couponType),
            discountCodes: item?.discountCodes,
            quantity: item?.quantity,
            status: Utils.capitalizeFirstLetter(item?.status),
            expiryDate: item?.expiryDate
          }));
        setCouponDatas(formattedData || []);
        setTotalItems(response?.total);
      }
    } catch (error) {
      console.error(error);
      return;
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    fetchCouponData((page - 1) * itemsPerPage, itemsPerPage);
  }, [page]);

  const handleDeleteCoupon = (couponID) => {
    setDeleteCouponID(couponID);
    setIsDeleteCoupon(!isDeleteCoupon);
  };
  // FUNCTION TO DELETE COUPON
  const deleteCoupon = async (couponId) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot();
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    try {
      dispatch(showLoader());
      const requestData = {
        couponId
      };
      const response = await Utils.parseResponse(
        new RewardsService().deleteCoupon(requestData)
      );
      if (response) {
        Utils.successToastMessage(
          successMessageConstant.COUPON_DELETE_SUCCESSFULLY
        );

        // Fetch the updated total count after deleting the coupon
        const updatedTotalItems = totalItems - 1;

        // Calculate the new page value based on the updated total items
        const maxPages = Math.ceil(updatedTotalItems / itemsPerPage);
        let newPage = page;

        // Check if current page is the first page and there's only one element
        if (page === 1 && updatedTotalItems >= 0) {
          newPage = 1; // Keep page value unchanged
        } else if (page > maxPages) {
          newPage = maxPages; // Adjust the page value to the maximum available page
        }

        // Fetch data with the updated skip value
        fetchCouponData((newPage - 1) * itemsPerPage, itemsPerPage);
      }
    } catch (error) {
      Utils.failureToastMessage(error?.message);
    } finally {
      handleDeleteCoupon();
      dispatch(hideLoader());
    }
  };

  return (
    <Root className="mt-5 flex w-full flex-col px-4 md:pl-12.5 md:pr-7">
      <p className="font-PoppinsSemiBold text-ft22-26.82-0.4 text-black-20">
        Coupon Inventory
      </p>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between">
        <div className="mr-4 mt-2 hidden md:inline-block">
          <p className="font-PoppinsRegular text-ft15 text-grey-575757">
            Create an inventory for different variations of discounts you intend
            to offer users in exchange for providing consents.{" "}
          </p>
          <p className="pt-1 font-PoppinsRegular text-ft15 text-grey-575757">
            Rewards will not be available until there are coupons in the
            inventory.
          </p>
        </div>
        <div className="mt-2 md:hidden">
          <p className="font-PoppinsRegular text-ft15-22.5-0.2 text-grey-575757">
            Create an inventory for different variations of discounts you intend
            to offer users in exchange for providing consents. Rewards will not
            be available until there are coupons in the inventory.
          </p>
        </div>
        <div>
          <button
            onClick={() => {
              togglePopup(setIsEdit(false));
            }}
            className="mt-4 h-9.5 w-121px rounded bg-purple-6C36B0 text-white md:mt-0"
          >
            <span className="font-MontserratMedium text-ft15-26.25 text-white">
              Add Coupon
            </span>
          </button>
        </div>
      </div>
      {showPopup && (
        <AddCoupon
          handleClose={togglePopup}
          item={editValues}
          isEdit={isEdit}
          fetchCouponData={fetchCouponData}
          page={page}
          itemsPerPage={itemsPerPage}
        />
      )}
      {showPopups && (
        <CouponCodes codes={editCodes} handleClose={togglePopups} />
      )}
      {isDeleteCoupon && (
        <DeleteCoupon
          deleteCoupon={deleteCoupon}
          deleteCouponID={deleteCouponID}
          handleDeleteCoupon={handleDeleteCoupon}
        />
      )}
      {couponDatas?.length > 0 ? (
        <TableContainer
          className="mt-7.5 w-full whitespace-nowrap rounded border border-b-0 border-grey-D9D9D9 md:mt-11.5"
          style={{ scrollbarWidth: "thin" }}
        >
          <Table>
            <TableHead>
              <TableRow className={classes.tableHead}>
                <TableCell>
                  <p className="font-MontserratSemiBold text-ft16-19.5 text-black-000000">
                    Discount Value
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-MontserratSemiBold text-ft16-19.5 text-black-000000">
                    Coupon Type
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-MontserratSemiBold text-ft16-19.5 text-black-000000">
                    Discount Code
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-MontserratSemiBold text-ft16-19.5 text-black-000000">
                    Inventory
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-MontserratSemiBold text-ft16-19.5 text-black-000000">
                    Expiry
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-MontserratSemiBold text-ft16-19.5 text-black-000000">
                    Status
                  </p>
                </TableCell>

                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {couponDatas.map((item) => (
                <TableRow key={item.id} className={classes.tableBody}>
                  <TableCell>
                    <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                      {Utils.isPurelyNumeric(item.discountValue)
                        ? `${item.discountValue}%`
                        : item.discountValue}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                      {item.couponType}
                    </p>
                  </TableCell>
                  <TableCell>
                    {Array.isArray(item.discountCodes) &&
                    item.discountCodes.length > 1 ? (
                      <span
                        className="cursor-pointer text-purple-6C36B0"
                        onClick={() => togglePopups(item.discountCodes)}
                      >
                        <p className="font-PoppinsRegular text-ft15-26.25 text-purple-6C36B0">
                          View Codes
                        </p>
                      </span>
                    ) : (
                      <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                        {Array.isArray(item.discountCodes)
                          ? item.discountCodes[0]
                          : item.discountCodes}
                      </p>
                    )}
                  </TableCell>

                  <TableCell>
                    <p
                      className={`font-PoppinsRegular text-ft15-26.25 text-black-000000 ${
                        item.quantity < 10 ? "text-red-10" : ""
                      }`}
                    >
                      {item.quantity}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                      {item?.expiryDate &&
                        moment(item.expiryDate)
                          .utcOffset("+05:30")
                          .format("DD MMMM YYYY")}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                      {item?.status}
                    </p>
                  </TableCell>

                  <TableCell className="flex gap-2">
                    <div className="flex flex-row justify-end gap-3 pr-9">
                      {item.couponType === couponTypes.UNIQUE.VALUE && (
                        <img
                          src="/images/pencil.svg"
                          alt="Pencil Icon"
                          onClick={() => {
                            togglePopup(item);
                            setIsEdit(true);
                          }}
                          className="cursor-pointer"
                        />
                      )}
                      <img
                        src="/images/delete.svg"
                        alt="Delete Icon"
                        onClick={() => handleDeleteCoupon(item?.id)}
                        className="cursor-pointer"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div
          // style={{ height: window.innerHeight - 216 }} // Managing height here using window.innerHeight
          className="mt-8 grid h-69 w-full place-items-center rounded border border-solid border-grey-D9D9D9"
        >
          <div className="flex flex-col items-center gap-9">
            <img src="/images/no-coupon.svg" alt="No Coupon" />
            <div className="font-PoppinsMedium text-ft22-33-0.4 text-grey-767676">
              No Coupon Created Yet
            </div>
          </div>
        </div>
      )}

      {totalItems > itemsPerPage && (
        <CustomPagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={page}
          handleChangePage={handleChangePage}
        />
      )}
    </Root>
  );
}

export default CouponInventory;
